import {useAccessPolicy} from '../useAccessPolicy';
import {datasetFileName,datasetOption} from "../datasetPresentation";
import {profilesForLanguages,LANGUAGE_NAMES,languageCode,availableOptionalKeys,resolveEvaluationSelection} from "../evaluationSelection";
import { LightSelect as Select } from "./select/LightSelect";
import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Alert,
  App,
  Button,
  Card,
  Checkbox,
  Descriptions,
  Drawer,
  Form,
  Input,
  InputNumber,
  Radio,
  Space,
  Steps,
  Tag,
  Typography } from "antd";
import { RefreshCw } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { api } from "../api";
import { useDatasets } from "../query";
import {
  estimateTaskCount,
  type PlanRequest,
  type Provider,
} from "../types";

interface RunForm {
  runName: string;
  runId: string;
  datasetId: string;
  provider: Provider;
  lunaModel:"luna-tts"|"luna-tts-character";
  lunaScene:"normal"|"whisper";
  lunaExpressivity:"normal"|"strong";
  lunaVolume:number;
  lunaNormalization:boolean;
  voiceIds: string[];
  speed: number;
  repeats: number;
  concurrency: number;
  audioFormat: "wav" | "mp3" | "pcm";
  assignmentMode: "cartesian" | "round_robin" | "paired";
  optionalMetricKeys:string[];
  evaluationProfileId: string;
  qualityGateId: string;
  priority: "low" | "medium" | "high";
}

const stepFields: Array<Array<keyof RunForm>> = [
  ["runName", "runId", "datasetId"],
  ["provider", "voiceIds"],
  ["speed", "repeats", "concurrency", "audioFormat", "assignmentMode"],
  ["evaluationProfileId", "qualityGateId", "priority"],
  [],
];

const createRunId = () => `RUN-${new Date().toISOString().replace(/[-:TZ.]/g, "").slice(0, 14)}`;

export function RunCreateDrawer({
  open,
  onClose,
  initialDatasetId,
}: {
  open: boolean;
  onClose: () => void;
  initialDatasetId?: string;
}) {
  const access=useAccessPolicy();
  const [current, setCurrent] = useState(0);
  const initializedForOpen = useRef(false);
  const [form] = Form.useForm<RunForm>();
  const { message } = App.useApp();
  const navigate = useNavigate();

  const queryClient = useQueryClient();
  const { data: datasets = [] } = useDatasets();
  const audioFormat=Form.useWatch("audioFormat",form);
  const provider = Form.useWatch("provider", form) ?? "vuilabs";
  const lunaModel=Form.useWatch("lunaModel",form)??"luna-tts";
  const isCharacter=provider==="vuilabs"&&lunaModel==="luna-tts-character";
  const selectedDatasetId = Form.useWatch("datasetId", form);
  const selectedVoices = Form.useWatch("voiceIds", form) ?? [];
  const repeats = Form.useWatch("repeats", form) ?? 1;
  const speed = Form.useWatch("speed", form) ?? 1;
  const assignmentMode = Form.useWatch("assignmentMode", form) ?? "cartesian";
  const evaluationProfileId = Form.useWatch("evaluationProfileId", form);
  const optionalMetricKeys=Form.useWatch("optionalMetricKeys",form) as string[]|undefined;
  const selectedDataset = datasets.find((item) => item.id === selectedDatasetId);
  const datasetLanguages=Object.keys(selectedDataset?.summary.languages??{});
  const languageSignature=datasetLanguages.map(languageCode).sort().join(',');
  const voices = useQuery({
    queryKey: ["voices", provider],
    queryFn: () => api.voices(provider),
    enabled: open,
    refetchInterval: (query) => {
      if (!open) return false;
      return query.state.data?.stale ? 2_000 : 60_000;
    },
    refetchOnWindowFocus: true,
  });
  const evaluationProfiles = useQuery({
    queryKey: ["evaluation-profiles"],
    queryFn: api.evaluationProfiles,
    enabled: open,
  });
  const qualityGates = useQuery({
    queryKey: ["quality-gates"],
    queryFn: api.qualityGates,
    enabled: open,
  });
  const selectedProfile = profilesForLanguages(evaluationProfiles.data?.items??[],datasetLanguages).find(
    (item) => item.id === evaluationProfileId,
  );
  const enabledEvaluators = new Set(
    (selectedProfile?.metrics ?? [])
      .filter((metric) => metric.available && (metric.required||optionalMetricKeys===undefined||optionalMetricKeys.includes(metric.metric_key)))
      .map((metric) => metric.evaluator_key),
  );
  const selectedGate=qualityGates.data?.items.find(g=>g.id===selectedProfile?.quality_gate_policy_version_id);
  const selectedMetrics=selectedProfile?.metrics.filter(m=>m.required||(optionalMetricKeys??availableOptionalKeys(selectedProfile)).includes(m.metric_key))??[];
  let selectionError:string|undefined;
  if(evaluationProfiles.data&&qualityGates.data){try{resolveEvaluationSelection(evaluationProfiles.data.items,qualityGates.data.items,evaluationProfileId,datasetLanguages,optionalMetricKeys);}catch(error){selectionError=error instanceof Error?error.message:'评测配置不一致';}}
  const profileAsrEnabled = enabledEvaluators.has("asr.content_accuracy")
    || enabledEvaluators.has("asr.pause_screening") || enabledEvaluators.has("asr.speech_rate");
  const refreshCatalog = useMutation({
    mutationFn: () => api.voices(provider, true),
    onSuccess: (catalog) => {
      queryClient.setQueryData(["voices", provider], catalog);
      if (catalog.stale) message.warning(catalog.warning || "接口刷新失败，继续使用最近成功目录");
      else message.success("声线目录已与接口同步");
    },
    onError: (error) => message.error(error instanceof Error ? error.message : "声线目录刷新失败"),
  });
  const catalogVoiceIds = useMemo(
    () => new Set((voices.data?.voices ?? []).map((voice) => voice.voice_id)),
    [voices.data?.voices],
  );
  const unavailableSelectedVoices = voices.data
    ? selectedVoices.filter((voiceId) => !catalogVoiceIds.has(voiceId))
    : [];
  const caseCount = selectedDataset?.summary.enabled_cases ?? 0;
  const planned = estimateTaskCount(caseCount, selectedVoices.length, repeats, assignmentMode);
  const pairedInvalid = assignmentMode === "paired" && caseCount !== selectedVoices.length;

  useEffect(() => {
    if (!open) {
      initializedForOpen.current = false;
      return;
    }
    if (!initializedForOpen.current) {
      form.resetFields();
      form.setFieldsValue({
        runName: "TTS 公网模型回归评测",
        runId: createRunId(),
        datasetId: initialDatasetId ?? datasets[0]?.id,
        provider: "vuilabs",
        lunaModel:"luna-tts",lunaScene:"normal",lunaExpressivity:"normal",lunaVolume:0,lunaNormalization:false,
        voiceIds: [],
        speed: 1,
        repeats: 1,
        concurrency: 2,
        audioFormat: "wav",
        assignmentMode: "cartesian",
        priority: "medium",
      });
      setCurrent(0);
      initializedForOpen.current = true;
    }
    if (!form.getFieldValue("datasetId") && (initialDatasetId ?? datasets[0]?.id)) {
      form.setFieldValue("datasetId", initialDatasetId ?? datasets[0]?.id);
    }
    if(!evaluationProfiles.data||!qualityGates.data)return;
    const actualDataset=datasets.find(d=>d.id===form.getFieldValue('datasetId'));
    const profiles = profilesForLanguages(evaluationProfiles.data.items,Object.keys(actualDataset?.summary.languages??{}));
    const currentProfile = profiles.find(
      (item) => item.id === form.getFieldValue("evaluationProfileId") && item.runnable,
    );
    const profile = currentProfile
      ?? profiles.find((item) => item.profile_key === "tts.standard"
        && item.current_deployment
        && item.runnable)
      ?? profiles.find((item) => item.current_deployment && item.runnable)
      ?? profiles.find((item) => item.runnable);
    if (profile?.id !== form.getFieldValue("evaluationProfileId")) {
      form.setFieldValue("evaluationProfileId", profile?.id);
      form.setFieldValue("optionalMetricKeys",availableOptionalKeys(profile));
    }
    const previousKeys=form.getFieldValue('optionalMetricKeys') as string[]|undefined;
    const validKeys=availableOptionalKeys(profile,previousKeys);
    if(JSON.stringify(previousKeys)!==JSON.stringify(validKeys))form.setFieldValue('optionalMetricKeys',validKeys);
    const gateId=profile?.quality_gate_policy_version_id;
    if (gateId !== form.getFieldValue("qualityGateId")) form.setFieldValue("qualityGateId",gateId??undefined);

  }, [datasets, evaluationProfiles.data?.items, form, initialDatasetId, open, qualityGates.data?.items,languageSignature,selectedDatasetId,evaluationProfileId,optionalMetricKeys]);

  useEffect(() => {
    form.setFieldValue("voiceIds", []);
    if (provider === "yumemi") form.setFieldValue("speed", 1);
  }, [form, provider]);

  useEffect(()=>{
    if(provider!=="vuilabs")return;
    form.setFieldsValue({voiceIds:[],speed:1,audioFormat:"wav",lunaScene:"normal",lunaExpressivity:"normal",lunaNormalization:false});
  },[form,provider,lunaModel]);

  useEffect(()=>{if(open&&profileAsrEnabled&&audioFormat&&audioFormat!=='wav'){
    form.setFieldValue('audioFormat','wav');message.info('已选择 ASR 指标，音频格式已同步为 WAV');
  }},[open,profileAsrEnabled,audioFormat,form,message]);

  const planMutation = useMutation({
    mutationFn: (values: RunForm) => {
      const dataset=datasets.find(d=>d.id===values.datasetId);
      const selection=resolveEvaluationSelection(evaluationProfiles.data?.items??[],qualityGates.data?.items??[],values.evaluationProfileId,Object.keys(dataset?.summary.languages??{}),values.optionalMetricKeys);
      if(selection.gate.id!==values.qualityGateId)throw new Error('质量要求已变化，请重新核对');
      if(selection.asr&&values.audioFormat!=='wav')throw new Error('已选择 ASR 指标，请使用 WAV 格式');
      const body: PlanRequest = {
        run_name: values.runName,
        dataset_id: values.datasetId,
        run_id: values.runId,
        provider: values.provider,
        ...(values.provider==="vuilabs"?{luna:{model_id:values.lunaModel??"luna-tts",volume:values.lunaVolume??0,scene:values.lunaScene??"normal",expressivity:values.lunaExpressivity??"normal",text_normalization:values.lunaNormalization??false}}:{}),
        voice_ids: values.voiceIds,
        repeats: values.repeats,
        concurrency: values.concurrency,
        assignment_mode: values.assignmentMode,
        speed: values.speed,
        audio_format: values.audioFormat,
        quality_detection: true,
        mos_enabled: selection.mos,
        asr_enabled: selection.asr,
        selected_optional_metric_keys:selection.optionalKeys,
        evaluation_profile_version_id: values.evaluationProfileId,
        quality_gate_policy_version_id: selection.gate.id,
        priority: values.priority,
        voice_catalog_version: voices.data?.catalog_version ?? "manual",
        voice_catalog_fetched_at: voices.data?.fetched_at ?? new Date().toISOString(),
        voice_catalog_stale: voices.data?.stale ?? false,
      };
      return api.plan(body);
    },
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey: ["runs"] });
      message.success(`任务预览已生成，共计划 ${result.summary.total.toLocaleString()} 个任务`);
      setCurrent(0);
      onClose();
      navigate(`/runs/${encodeURIComponent(result.run.id)}`);
    },
    onError: (error) => message.error(error instanceof Error ? error.message : "创建失败"),
  });

  const steps = useMemo(
    () => ["选择数据集", "平台与声线", "运行参数", "评测内容", "预览确认"],
    [],
  );

  const closeDrawer = () => {
    setCurrent(0);
    onClose();
  };

  const goNext = async () => {
    await form.validateFields(stepFields[current]);
    if (current === 2 && pairedInvalid) {
      message.error(`一一配对要求 Case 数与声线数相同（当前 ${caseCount} / ${selectedVoices.length}）`);
      return;
    }
    if(current===3&&selectionError){message.error(selectionError);return;}
    setCurrent((value) => Math.min(value + 1, steps.length - 1));
  };

  if(open&&!access.can('create_run'))return <Drawer open={open} onClose={onClose} title="创建评测"><Alert showIcon type="info" message="当前没有创建评测的项目权限" description="请联系管理员分配项目或调整授权。"/></Drawer>;
  return (
    <Drawer
      open={open}
      onClose={closeDrawer}
      width={620}
      destroyOnHidden
      title={<div><strong>创建批量评测</strong><div className="drawer-subtitle">选择测试文本、声线和指标，核对任务数量后开始</div></div>}
      footer={
        <div className="drawer-footer">
          <Button onClick={closeDrawer}>取消</Button>
          <Space>
            {current > 0 && <Button onClick={() => setCurrent((value) => value - 1)}>上一步</Button>}
            {current < steps.length - 1 ? (
              <Button type="primary" onClick={goNext}>下一步</Button>
            ) : (
              <Button
                type="primary"
                loading={planMutation.isPending}
                disabled={pairedInvalid || planned > 10_000 || !!selectionError || evaluationProfiles.isFetching || qualityGates.isFetching}
                onClick={() => form.validateFields().then((values) => planMutation.mutate(values))}
              >
                预览任务
              </Button>
            )}
          </Space>
        </div>
      }
    >
      <Steps
        current={current}
        size="small"
        responsive={false}
        items={steps.map((title) => ({ title }))}
        className="run-steps"
      />
      <Form form={form} layout="vertical" requiredMark="optional" className="run-form">
        <div hidden={current !== 0}>
          <Form.Item name="runName" label="批次名称" rules={[{ required: true }]}>
            <Input maxLength={64} showCount placeholder="例如：Luna 广告场景 · 3 条声线" />
          </Form.Item>
          <details><summary>高级信息：批次编号（系统已自动填写）</summary><Form.Item name="runId" label="批次编号" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          </details><Form.Item name="datasetId" label="数据集文件与版本" rules={[{ required: true }]}>
            <Select
              options={datasets.map(datasetOption)}
                    showSearch optionFilterProp="label"
              placeholder="搜索文件名或版本号"
            />
          </Form.Item>
          {selectedDataset && (
            <Alert
              type="info"
              showIcon
              message={`已固定 ${datasetFileName(selectedDataset)}`}
              description={`${selectedDataset.summary.enabled_cases.toLocaleString()} 条启用文本；本次评测使用当前选定版本。`}
            />
          )}
        </div>

        <div hidden={current !== 1}>
          <Form.Item name="provider" label="测试平台" rules={[{ required: true }]}>
            <Radio.Group className="provider-grid">
              <Radio.Button value="yumemi"><b className="provider-dot yumemi">Y</b> Yumemi 公网 TTS</Radio.Button>
              <Radio.Button value="vuilabs"><b className="provider-dot vuilabs">V</b> VUILabs 公网 TTS</Radio.Button>
            </Radio.Group>
          </Form.Item>
          {provider==="vuilabs"&&<Form.Item name="lunaModel" label="VUILabs 模型" rules={[{required:true}]}><Select options={[{value:"luna-tts",label:"Luna-TTS · 系统 / 克隆声线"},{value:"luna-tts-character",label:"Luna-TTS Character · 角色声线"}]}/></Form.Item>}
          <div className="voice-catalog-toolbar">
            <div>
              <b>接口声线目录</b>
              {voices.data ? (
                <small>
                  {provider==="vuilabs"?"官方系统目录 + 项目自定义声线":voices.data.cache_source === "provider" ? "Provider 实时返回" : voices.data.cache_source === "redis" ? "Redis 短缓存" : "进程内降级缓存"}
                  {" · "}更新于 {new Date(voices.data.fetched_at).toLocaleString("zh-CN")}
                  {" · "}版本 {voices.data.catalog_version.slice(0, 8)}
                </small>
              ) : <small>正在读取 Provider 声线目录</small>}
            </div>
            <Button
              size="small"
              icon={<RefreshCw size={14} />}
              loading={refreshCatalog.isPending || voices.isFetching}
              onClick={() => refreshCatalog.mutate()}
            >
              与接口同步
            </Button>
          </div>
          {voices.error && (
            <Alert
              className="form-alert"
              type="error"
              showIcon
              message="无法读取声线目录"
              description={voices.error instanceof Error ? voices.error.message : "请稍后重试"}
            />
          )}
          {voices.data?.stale && (
            <Alert
              className="form-alert"
              type="warning"
              showIcon
              message={voices.data.refreshing ? "正在后台同步，当前先展示最近成功目录" : "当前展示最近成功目录"}
              description={voices.data.warning || `目录已缓存 ${voices.data.age_seconds} 秒；同步完成后页面会自动刷新。`}
            />
          )}
          <Form.Item
            required
            name="voiceIds"
            label="声线（多选）"
            rules={[{
              validator: async (_, value: string[] | undefined) => {
                if (!value?.length) throw new Error("至少选择一条声线");
                const missing = voices.data ? value.filter((voiceId) => !catalogVoiceIds.has(voiceId)) : [];
                if (missing.length) throw new Error(`所选声线已不在当前接口目录：${missing.join("、")}`);
              },
            }]}
          >
            {voices.data ? <Select mode="multiple" showSearch optionFilterProp="label" placeholder="搜索并选择声线" options={voices.data.voices.filter(voice=>provider!=="vuilabs"||voice.model_id===lunaModel).map(voice=>({value:voice.voice_id,label:voice.display_name||voice.voice_id,displayTitle:voice.voice_id,displayMeta:voice.model_id||voice.language||provider}))} /> : <Select mode="tags" tokenSeparators={[",", " "]} placeholder="目录不可用时可手动输入 voice_id，回车确认" />}
          </Form.Item>
          {unavailableSelectedVoices.length > 0 && (
            <Alert
              type="error"
              showIcon
              message="已选择的声线目录发生变化"
              description={`以下声线已下线或不可用，请重新选择：${unavailableSelectedVoices.join("、")}`}
            />
          )}
        </div>

        <div hidden={current !== 2} className="form-two-columns">
          <Form.Item name="speed" label="语速" rules={[{ required: true }]}>
            <InputNumber
              min={provider === "yumemi" || isCharacter ? 1 : 0.6}
              max={provider === "yumemi" || isCharacter ? 1 : 1.2}
              step={provider === "yumemi" ? 1 : 0.05}
              disabled={provider === "yumemi" || isCharacter}
              className="full-width"
            />
          </Form.Item>
          {provider==="vuilabs"&&<><Form.Item name="lunaVolume" label="音量"><InputNumber min={-5} max={5} step={0.5}/></Form.Item>
            {isCharacter?<><Form.Item name="lunaScene" label="场景"><Select options={[{value:"normal",label:"日常"},{value:"whisper",label:"耳边私语"}]}/></Form.Item><Form.Item name="lunaExpressivity" label="表现力"><Select options={[{value:"normal",label:"普通"},{value:"strong",label:"强"}]}/></Form.Item></>:<Form.Item name="lunaNormalization" label="文本归一化" valuePropName="checked" extra="默认关闭，便于按原始文本评测"><Checkbox>启用文本归一化</Checkbox></Form.Item>}
            <Alert className="form-span-two" type="info" showIcon message="每条最多 500 加权字符（中文每字计 2），输出为 WAV" description={isCharacter?"Character 使用专属角色声线，不支持调速、克隆声线或表达标签。":"Luna-TTS 支持系统和项目克隆声线，语速范围 0.6–1.2。"}/>
          </>}
          <Form.Item name="repeats" label="重复次数" rules={[{ required: true }]}>
            <InputNumber min={1} max={20} className="full-width" />
          </Form.Item>
          <Form.Item name="concurrency" label="并发数" rules={[{ required: true }]}>
            <InputNumber min={1} max={8} className="full-width" />
          </Form.Item>
          <Form.Item name="audioFormat" label="音频格式" rules={[{ required: true }]}>
            <Select options={(provider==="vuilabs"?["wav"]:["wav", "mp3", "pcm"]).map((value) => ({ value, label: value.toUpperCase() }))} />
          </Form.Item>
          <Form.Item name="assignmentMode" label="分配方式" className="form-span-two">
            <Select
              options={[
                { value: "cartesian", label: "全组合：每条文本 × 每条声线" },
                { value: "round_robin", label: "轮询分配" },
                { value: "paired", label: "一一配对" },
              ]}
            />
          </Form.Item>
          {pairedInvalid && (
            <Alert
              className="form-span-two"
              type="error"
              showIcon
              message={`无法一一配对：${caseCount} 个 Case，但选择了 ${selectedVoices.length} 条声线`}
            />
          )}
        </div>

        <div hidden={current !== 3}>
          {selectionError&&<Alert type="warning" showIcon message={selectionError} className="form-alert"/>}
          <Form.Item
            name="evaluationProfileId"
            label={`评测方案 · ${datasetLanguages.length?datasetLanguages.map(l=>LANGUAGE_NAMES[languageCode(l)]??l).join('、'):'语种未记录'}`}
            rules={[{ required: true, message: "请选择评测方案" }]}
          >
            <Select
              loading={evaluationProfiles.isLoading}
              options={profilesForLanguages(evaluationProfiles.data?.items ?? [],languageSignature?languageSignature.split(','):[]).map((profile) => ({
                value: profile.id,
                label: profile.display_name,
                disabled: !profile.runnable,
              }))}
              onChange={(value) => {
                const profile = evaluationProfiles.data?.items.find((item) => item.id === value);
                form.setFieldValue("qualityGateId", profile?.quality_gate_policy_version_id??undefined);
                form.setFieldValue("optionalMetricKeys",availableOptionalKeys(profile));

              }}
            />
          </Form.Item>
          {selectedProfile && <Card size="small" className="form-alert">
            <Space wrap>
              {selectedProfile.metrics.filter(m=>m.required).map((metric) => (
                <Tag
                  key={`${metric.metric_key}-${metric.metric_version}`}
                  color={metric.available ? metric.required ? "blue" : "cyan" : "default"}
                >
                  {metric.display_name} · {metric.required ? "必选" : "可选"} · {metric.available ? "启用" : "当前未部署"}
                </Tag>
              ))}
            </Space>
            <Form.Item name="optionalMetricKeys" label="可选指标" extra="未选择的指标不参与本次质量判定。" style={{marginTop:12,marginBottom:0}}><Checkbox.Group options={selectedProfile.metrics.filter(m=>!m.required).map(m=>({value:m.metric_key,label:m.display_name,disabled:!m.available}))}/></Form.Item>
            {selectedProfile.unavailable_optional_metrics.length > 0 && (
              <Typography.Paragraph type="secondary" style={{ margin: "10px 0 0" }}>
                当前不可用的可选指标不能勾选，不参与本次质量判定。
              </Typography.Paragraph>
            )}
          </Card>}
          <Form.Item
            name="qualityGateId"
            label="方案对应的质量要求"
            extra="由所选方案自动带出，创建任务时不能单独替换。"
            rules={[{ required: true, message: "当前系统质量要求不可用，请联系管理员" }]}
            className="top-gap"
          >
            <Select
              loading={qualityGates.isLoading}
              disabled
              options={selectedGate?[{value:selectedGate.id,label:selectedGate.display_name}]:[]}

            />
          </Form.Item>

          <Alert
            type="info"
            showIcon
            message="指标按方案执行，基础音质检查采用系统固定规则"
            description={`基础规则机筛始终执行，无需在自选方案重复添加；阈值由系统管理员维护。当前将启用：规则机筛${selectedMetrics.length ? "；所选指标："+selectedMetrics.map(m=>m.display_name).join("、") : ""}。确认后会保留本次使用的规则，方便后续核对。`}
          />
          <Form.Item name="priority" label="队列优先级" className="top-gap">
            <Radio.Group options={[{ value: "low", label: "低" }, { value: "medium", label: "中" }, { value: "high", label: "高" }]} />
          </Form.Item>
        </div>

        <div hidden={current !== 4}>
          <Alert
            type="success"
            showIcon
            message="预览不会生成音频，也不会产生 TTS 费用"
            description="先核对文本、声线、重复次数和总任务数；确认启动后才会生成音频。"
            className="form-alert"
          />
          <Descriptions column={1} bordered size="small">
            <Descriptions.Item label="数据集版本">{selectedDataset ? datasetFileName(selectedDataset) : "—"}</Descriptions.Item>
            <Descriptions.Item label="评测语种">{datasetLanguages.map(l=>LANGUAGE_NAMES[languageCode(l)]??l).join("、")||"未记录"}</Descriptions.Item>
            <Descriptions.Item label="平台">{provider === "yumemi" ? "Yumemi" : "VUILabs"}</Descriptions.Item>
            <Descriptions.Item label="声线">{selectedVoices.length} 条</Descriptions.Item>
            <Descriptions.Item label="语速 / 重复">{speed} / {repeats} 次</Descriptions.Item>
            <Descriptions.Item label="分配方式">{assignmentMode==='cartesian'?'每条文本 × 全部所选声线':assignmentMode==='round_robin'?'将文本轮流分配给声线':'文本与声线一一配对'}</Descriptions.Item>
            <Descriptions.Item label="评测方案">{selectedProfile ? selectedProfile.display_name : "—"}</Descriptions.Item>
            <Descriptions.Item label="本次指标">{selectedMetrics.map(m=>m.display_name).join('、')||'基础音质检查'}</Descriptions.Item>
            <Descriptions.Item label="未选可选项">{selectedProfile?.metrics.filter(m=>!m.required&&!selectedMetrics.includes(m)).map(m=>m.display_name).join('、')||'无'}</Descriptions.Item>
            <Descriptions.Item label="方案质量要求">{selectedGate?.display_name??'—'}</Descriptions.Item>
            <Descriptions.Item label="预计任务量">
              <Typography.Text strong className="planned-count">{planned.toLocaleString()}</Typography.Text>
            </Descriptions.Item>
          </Descriptions>
          <details className="technical-details"><summary>高级信息：规则与声线目录版本</summary><p>评测方案版本：{selectedProfile?.content_sha256??'—'}</p><p>声线目录版本：{voices.data?.catalog_version??'—'}</p></details>
          {planned > 10_000 && <Alert className="top-gap" type="error" showIcon message="本批任务数超过 10,000" description="请减少声线或重复次数，或者将数据集拆成多批。" />}
        </div>
      </Form>
    </Drawer>
  );
}
